import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Plus,
  Edit2,
  Trash2,
  AlertTriangle,
  Tag,
  Info,
  X,
  Calendar,
  Image,
  Link,
  Users,
  Eye,
  EyeOff,
  Megaphone,
  Upload,
  Loader2,
  Lock
} from 'lucide-react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import toast from 'react-hot-toast';
import { compressImage } from '../../utils/imageUtils';
import { Announcement, AnnouncementType, AnnouncementAudience } from '../../types';

interface AnnouncementFormData {
  title: string;
  body: string;
  imageUrl: string;
  type: AnnouncementType;
  ctaText: string;
  ctaLink: string;
  expirationDate: string;
  targetAudience: AnnouncementAudience;
  isActive: boolean;
}

const defaultFormData: AnnouncementFormData = {
  title: '',
  body: '',
  imageUrl: '',
  type: 'info',
  ctaText: '',
  ctaLink: '',
  expirationDate: '',
  targetAudience: 'all',
  isActive: true
};

const AnnouncementManagementPage: React.FC = () => {
  const { businessId, currentBusiness } = useBusiness();
  const { config: systemConfig } = useSystemConfig();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState<AnnouncementFormData>(defaultFormData);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const hasCommunications = useMemo(() => {
    const plan = (currentBusiness?.subscription?.plan || 'free') as 'free' | 'basic' | 'pro';
    return systemConfig.plans[plan]?.hasCommunications ?? false;
  }, [currentBusiness, systemConfig]);

  // Subir imagen
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !businessId) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    setUploadingImage(true);

    try {
      // Preview local mientras sube
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target?.result as string);
      reader.readAsDataURL(file);

      // Subir a Storage
      const compressedFile = await compressImage(file);
      const fileName = `announcements/${businessId}/${Date.now()}_${compressedFile.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, compressedFile);
      const downloadUrl = await getDownloadURL(storageRef);

      setFormData(prev => ({ ...prev, imageUrl: downloadUrl }));
      setImagePreview(downloadUrl);
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir la imagen');
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  // Eliminar imagen
  const removeImage = () => {
    setFormData(prev => ({ ...prev, imageUrl: '' }));
    setImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Cargar anuncios
  const loadAnnouncements = async () => {
    if (!businessId) return;

    try {
      const q = query(
        collection(db, 'announcements'),
        where('businessId', '==', businessId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Announcement[];

      setAnnouncements(data);
    } catch (error) {
      console.error('Error cargando anuncios:', error);
      toast.error('Error al cargar anuncios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, [businessId]);

  // Crear anuncio
  const handleCreate = async () => {
    if (!businessId) return;
    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error('El título y el contenido son obligatorios');
      return;
    }

    try {
      await addDoc(collection(db, 'announcements'), {
        businessId,
        title: formData.title.trim(),
        body: formData.body.trim(),
        imageUrl: formData.imageUrl.trim() || null,
        type: formData.type,
        ctaText: formData.ctaText.trim() || null,
        ctaLink: formData.ctaLink.trim() || null,
        expirationDate: formData.expirationDate || null,
        targetAudience: formData.targetAudience,
        isActive: formData.isActive,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      toast.success('Anuncio creado exitosamente');
      resetForm();
      loadAnnouncements();
    } catch (error) {
      console.error('Error creando anuncio:', error);
      toast.error('Error al crear el anuncio');
    }
  };

  // Actualizar anuncio
  const handleUpdate = async () => {
    if (!editingAnnouncement) return;
    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error('El título y el contenido son obligatorios');
      return;
    }

    try {
      await updateDoc(doc(db, 'announcements', editingAnnouncement.id), {
        title: formData.title.trim(),
        body: formData.body.trim(),
        imageUrl: formData.imageUrl.trim() || null,
        type: formData.type,
        ctaText: formData.ctaText.trim() || null,
        ctaLink: formData.ctaLink.trim() || null,
        expirationDate: formData.expirationDate || null,
        targetAudience: formData.targetAudience,
        isActive: formData.isActive,
        updatedAt: serverTimestamp()
      });

      toast.success('Anuncio actualizado');
      resetForm();
      loadAnnouncements();
    } catch (error) {
      console.error('Error actualizando anuncio:', error);
      toast.error('Error al actualizar el anuncio');
    }
  };

  // Eliminar anuncio
  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este anuncio?')) return;

    try {
      await deleteDoc(doc(db, 'announcements', id));
      toast.success('Anuncio eliminado');
      loadAnnouncements();
    } catch (error) {
      console.error('Error eliminando anuncio:', error);
      toast.error('Error al eliminar el anuncio');
    }
  };

  // Toggle activo/inactivo
  const handleToggleActive = async (announcement: Announcement) => {
    try {
      await updateDoc(doc(db, 'announcements', announcement.id), {
        isActive: !announcement.isActive,
        updatedAt: serverTimestamp()
      });
      toast.success(announcement.isActive ? 'Anuncio desactivado' : 'Anuncio activado');
      loadAnnouncements();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cambiar el estado');
    }
  };

  // Editar
  const startEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      body: announcement.body,
      imageUrl: announcement.imageUrl || '',
      type: announcement.type,
      ctaText: announcement.ctaText || '',
      ctaLink: announcement.ctaLink || '',
      expirationDate: announcement.expirationDate || '',
      targetAudience: announcement.targetAudience,
      isActive: announcement.isActive
    });
    setImagePreview(announcement.imageUrl || null);
    setShowForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingAnnouncement(null);
    setImagePreview(null);
    setShowForm(false);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Get type config
  const getTypeConfig = (type: AnnouncementType) => {
    switch (type) {
      case 'urgent':
        return {
          icon: <AlertTriangle className="w-5 h-5" />,
          color: 'bg-red-500/20 text-red-400 border-red-500/50',
          label: 'Urgente',
          gradient: 'from-red-600 to-rose-600'
        };
      case 'promo':
        return {
          icon: <Tag className="w-5 h-5" />,
          color: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
          label: 'Promoción',
          gradient: 'from-amber-500 to-orange-500'
        };
      case 'info':
      default:
        return {
          icon: <Info className="w-5 h-5" />,
          color: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
          label: 'Información',
          gradient: 'from-blue-500 to-cyan-500'
        };
    }
  };

  // Check if expired
  const isExpired = (expirationDate?: string) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  // Deep link options
  const deepLinkOptions = [
    { value: '', label: 'Sin enlace' },
    { value: '/calendar', label: 'Reservar clase' },
    { value: '/memberships', label: 'Membresías y Bonos' },
    { value: '/classes', label: 'Ver clases' },
    { value: '/instructors', label: 'Ver instructores' },
    { value: '/profile', label: 'Mi perfil' },
    { value: '/contact', label: 'Contacto' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!hasCommunications) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-6">
        <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Función No Disponible</h2>
        <p className="text-gray-400 max-w-md mb-6">
          La gestión de anuncios y comunicaciones no está disponible en tu plan actual.
          Actualiza tu plan para comunicarte con tus usuarios.
        </p>
        <Button
          onClick={() => window.location.href = '/admin/settings'}
          className="bg-gradient-to-r from-amber-500 to-orange-500"
        >
          Ver Planes
        </Button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestión de Anuncios</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Megaphone className="w-8 h-8 text-amber-400" />
              Gestión de Anuncios
            </h1>
            <p className="text-gray-400 mt-1">Comunica noticias, promociones y urgencias a tus usuarios</p>
          </div>
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nuevo Anuncio
            </Button>
          )}
        </div>

        {/* Formulario */}
        {showForm && (
          <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingAnnouncement ? 'Editar Anuncio' : 'Crear Nuevo Anuncio'}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna izquierda */}
              <div className="space-y-4">
                {/* Tipo de anuncio */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Tipo de Anuncio *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {(['info', 'promo', 'urgent'] as AnnouncementType[]).map((type) => {
                      const config = getTypeConfig(type);
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData({ ...formData, type })}
                          className={`p-3 rounded-xl border-2 transition-all ${formData.type === type
                            ? `${config.color} border-current`
                            : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                            }`}
                        >
                          <div className="flex flex-col items-center gap-2">
                            {config.icon}
                            <span className="text-xs font-medium">{config.label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Título */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Título *
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ej: ¡Nueva promoción de verano!"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                {/* Contenido */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Contenido *
                  </label>
                  <textarea
                    value={formData.body}
                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                    placeholder="Describe el anuncio..."
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Imagen */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Image className="w-4 h-4 inline mr-1" />
                    Imagen (opcional)
                  </label>

                  <input
                    type="file"
                    ref={imageInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />

                  {(formData.imageUrl || imagePreview) ? (
                    <div className="relative">
                      <div className="rounded-xl overflow-hidden h-40 bg-gray-800">
                        <img
                          src={imagePreview || formData.imageUrl}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute bottom-2 right-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="p-2 bg-gray-900/80 hover:bg-gray-900 rounded-lg text-white transition-colors"
                        >
                          {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={removeImage}
                          className="p-2 bg-red-600/80 hover:bg-red-600 rounded-lg text-white transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="w-full h-40 border-2 border-dashed border-gray-700 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-amber-500/50 hover:bg-gray-800/50 transition-colors"
                    >
                      {uploadingImage ? (
                        <>
                          <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
                          <span className="text-gray-400 text-sm">Subiendo...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-500" />
                          <span className="text-gray-400 text-sm">Haz clic para subir imagen</span>
                          <span className="text-gray-500 text-xs">PNG, JPG</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Columna derecha */}
              <div className="space-y-4">
                {/* Botón de acción */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Link className="w-4 h-4 inline mr-1" />
                    Botón de Acción (opcional)
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={formData.ctaText}
                      onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })}
                      placeholder="Texto del botón"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <select
                      value={formData.ctaLink}
                      onChange={(e) => setFormData({ ...formData, ctaLink: e.target.value })}
                      className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-amber-500"
                    >
                      {deepLinkOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Fecha de expiración */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Fecha de Expiración (opcional)
                  </label>
                  <Input
                    type="date"
                    value={formData.expirationDate}
                    onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El anuncio dejará de mostrarse después de esta fecha
                  </p>
                </div>

                {/* Audiencia */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Users className="w-4 h-4 inline mr-1" />
                    Audiencia
                  </label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value as AnnouncementAudience })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="all">Todos los usuarios</option>
                    <option value="members_only">Solo miembros activos</option>
                  </select>
                </div>

                {/* Estado activo */}
                <div className="flex items-center gap-3 p-4 bg-gray-800 rounded-xl">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-amber-500 focus:ring-amber-500"
                  />
                  <label htmlFor="isActive" className="text-gray-300">
                    Anuncio activo (visible para usuarios)
                  </label>
                </div>

                {/* Preview */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Vista previa</label>
                  <div className={`rounded-xl overflow-hidden border ${getTypeConfig(formData.type).color}`}>
                    {formData.imageUrl && (
                      <img
                        src={formData.imageUrl}
                        alt=""
                        className="w-full h-24 object-cover"
                        onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                      />
                    )}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        {getTypeConfig(formData.type).icon}
                        <span className="text-xs font-medium uppercase">
                          {getTypeConfig(formData.type).label}
                        </span>
                      </div>
                      <h3 className="font-bold text-white">{formData.title || 'Título del anuncio'}</h3>
                      <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                        {formData.body || 'Contenido del anuncio...'}
                      </p>
                      {formData.ctaText && (
                        <button className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r ${getTypeConfig(formData.type).gradient} text-white`}>
                          {formData.ctaText}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-3 mt-6 pt-6 border-t border-gray-800">
              <Button
                onClick={editingAnnouncement ? handleUpdate : handleCreate}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                {editingAnnouncement ? 'Actualizar Anuncio' : 'Crear Anuncio'}
              </Button>
              <Button variant="outline" onClick={resetForm} className="flex-1">
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Lista de anuncios */}
        {announcements.length === 0 && !showForm ? (
          <div className="text-center py-16 bg-gray-900/50 rounded-2xl border border-gray-800">
            <Megaphone className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No hay anuncios</h3>
            <p className="text-gray-400 mb-6">Crea tu primer anuncio para comunicarte con tus usuarios</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-amber-500 to-orange-500"
            >
              <Plus className="w-5 h-5 mr-2" />
              Crear Anuncio
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {announcements.map((announcement) => {
              const config = getTypeConfig(announcement.type);
              const expired = isExpired(announcement.expirationDate);

              return (
                <div
                  key={announcement.id}
                  className={`bg-gray-900 rounded-xl overflow-hidden border transition-all hover:shadow-lg ${!announcement.isActive || expired
                    ? 'border-gray-800 opacity-60'
                    : config.color
                    }`}
                >
                  {/* Image */}
                  {announcement.imageUrl && (
                    <div className="h-32 overflow-hidden">
                      <img
                        src={announcement.imageUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-4">
                    {/* Type badge & status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className={`flex items-center gap-2 px-2 py-1 rounded-lg ${config.color}`}>
                        {config.icon}
                        <span className="text-xs font-medium">{config.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {expired && (
                          <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded-lg">
                            Expirado
                          </span>
                        )}
                        {!announcement.isActive && (
                          <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded-lg">
                            Inactivo
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Title & body */}
                    <h3 className="font-bold text-white mb-2">{announcement.title}</h3>
                    <p className="text-sm text-gray-400 line-clamp-2 mb-3">{announcement.body}</p>

                    {/* CTA preview */}
                    {announcement.ctaText && (
                      <div className="mb-3">
                        <span className="text-xs text-gray-500">Botón: </span>
                        <span className="text-xs text-amber-400">{announcement.ctaText}</span>
                        {announcement.ctaLink && (
                          <span className="text-xs text-gray-500"> → {announcement.ctaLink}</span>
                        )}
                      </div>
                    )}

                    {/* Expiration */}
                    {announcement.expirationDate && (
                      <div className="text-xs text-gray-500 mb-3">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Expira: {new Date(announcement.expirationDate).toLocaleDateString()}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-3 border-t border-gray-800">
                      <button
                        onClick={() => handleToggleActive(announcement)}
                        className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${announcement.isActive
                          ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                          : 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                          }`}
                      >
                        {announcement.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        {announcement.isActive ? 'Ocultar' : 'Mostrar'}
                      </button>
                      <button
                        onClick={() => startEdit(announcement)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600/20 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-600/30 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="flex items-center justify-center px-3 py-2 bg-red-600/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-600/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default AnnouncementManagementPage;
